"use client";

import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
type Stage = {
  number: string;
  label: string;
  title: string;
  titleItalic: string;
  description: string;
  image: string;
  srcset: string;
  sizes: string;
  alt: string;
  details: {
    name: string;
    value: string;
  }[];
  reverse: boolean;
};
const stages: Stage[] = [{
  number: "01",
  label: "STAGE 01 — DATA AUDIT",
  title: "The initial ",
  titleItalic: "analysis.",
  description: "We begin by mapping your current revenue streams and OTA performance. Before implementing new strategies, we identify the gaps in your digital footprint and pricing architecture. This is where the foundation for your revenue growth is established.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/revenue_data_audit.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/revenue_data_audit.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/revenue_data_audit.png-wvc-sizes",
  alt: "Revenue manager analyzing hotel data dashboards",
  details: [{
    name: "FOCUS",
    value: "OTA parity, conversion rates"
  }, {
    name: "DURATION",
    value: "Three business days"
  }],
  reverse: false
}, {
  number: "02",
  label: "STAGE 02 — STRATEGY SESSION",
  title: "A collaborative ",
  titleItalic: "roadmap.",
  description: "We sit down with your management team to align on goals. Your property's unique market position, local competition, and guest demographics inform every decision. We build a bespoke revenue roadmap tailored to your specific operational needs.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/revenue_strategy_session.webp",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/revenue_strategy_session.webp-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/revenue_strategy_session.webp-wvc-sizes",
  alt: "Consultation meeting with hotel management team",
  details: [{
    name: "SETTING",
    value: "Virtual or on-site workshop"
  }, {
    name: "DURATION",
    value: "Two hours"
  }],
  reverse: true
}, {
  number: "03",
  label: "STAGE 03 — TECH STACK",
  title: "The digital ",
  titleItalic: "infrastructure.",
  description: "From Channel Managers to Booking Engines, we audit and recommend the right hospitality tech stack. We ensure your systems communicate seamlessly to prevent rate discrepancies and maximize direct booking potential across all platforms.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/revenue_tech_stack.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/revenue_tech_stack.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/revenue_tech_stack.png-wvc-sizes",
  alt: "Hospitality technology integration and software stack",
  details: [{
    name: "SYSTEMS",
    value: "PMS, CRS, Channel Manager"
  }, {
    name: "MEDIUM",
    value: "Cloud-based integration"
  }],
  reverse: false
}, {
  number: "04",
  label: "STAGE 04 — OTA OPTIMIZATION",
  title: "Refining your ",
  titleItalic: "listings.",
  description: "An essential step for visibility. We optimize your OTA content, imagery, and rate plans to improve ranking and conversion. There is no room for error in pricing; we ensure your listings are competitive and attractive to potential guests.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/revenue_ota_optimization.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/revenue_ota_optimization.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/revenue_ota_optimization.png-wvc-sizes",
  alt: "Optimizing hotel OTA listings for better visibility",
  details: [{
    name: "PLATFORMS",
    value: "Booking.com, Expedia, Agoda"
  }, {
    name: "DURATION",
    value: "One week"
  }],
  reverse: true
}, {
  number: "05",
  label: "STAGE 05 — RATE PARITY",
  title: "The parity ",
  titleItalic: "framework.",
  description: "We implement automated monitoring to ensure your direct rates are always competitive. We identify and resolve rate leakage, ensuring that your website remains the most attractive booking channel for your guests.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/revenue_rate_parity.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/revenue_rate_parity.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/revenue_rate_parity.png-wvc-sizes",
  alt: "Monitoring rate parity across multiple booking channels",
  details: [{
    name: "MONITORING",
    value: "Real-time rate tracking"
  }, {
    name: "DURATION",
    value: "Continuous"
  }],
  reverse: false
}, {
  number: "06",
  label: "STAGE 06 — PERFORMANCE TRACKING",
  title: "Data-driven ",
  titleItalic: "insights.",
  description: "We provide detailed reporting on RevPAR, ADR, and occupancy trends. Our insights help you understand guest behavior and market shifts, allowing for agile pricing adjustments that keep you ahead of the competition.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/revenue_performance_tracking.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/revenue_performance_tracking.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/revenue_performance_tracking.png-wvc-sizes",
  alt: "Analyzing hotel revenue performance metrics",
  details: [{
    name: "METRICS",
    value: "RevPAR, ADR, Occupancy"
  }, {
    name: "DURATION",
    value: "Monthly reporting"
  }],
  reverse: true
}, {
  number: "07",
  label: "STAGE 07 — CONTINUOUS GROWTH",
  title: "Optimization, scale, ",
  titleItalic: "success.",
  description: "Revenue management is an ongoing process. We continuously refine your strategy based on performance data, ensuring your hotel maintains its market share and profitability in an ever-changing hospitality landscape.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/revenue_continuous_growth.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/revenue_continuous_growth.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/revenue_continuous_growth.png-wvc-sizes",
  alt: "Long-term hotel revenue growth and success",
  details: [{
    name: "GOAL",
    value: "Maximized profitability"
  }, {
    name: "DURATION",
    value: "Ongoing partnership"
  }],
  reverse: false
}];
function StageSpread({
  stage,
  index
}: {
  stage: Stage;
  index: number;
}) {
  const numeralReveal = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const imageReveal = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  const textReveal = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  return <div data-index={index} className="relative">
      {index > 0 && <div className="flex justify-center pb-16 md:pb-24">
          <div className="h-24 md:h-32 w-px bg-border" />
        </div>}

      <div className="relative overflow-hidden py-16 md:py-24 lg:min-h-[80vh] flex items-center">
        <div ref={numeralReveal.ref} className={`pointer-events-none absolute top-1/2 -translate-y-1/2 ${stage.reverse ? "right-0 translate-x-1/4 md:translate-x-1/3" : "left-0 -translate-x-1/4 md:-translate-x-1/3"} font-serif italic leading-none select-none transition-all duration-1000 ease-out`} style={{
        fontSize: "clamp(8rem, 22vw, 18rem)",
        color: "var(--secondary)",
        opacity: numeralReveal.isVisible ? 0.18 : 0,
        transform: `translateY(-50%) ${stage.reverse ? "translateX(33%)" : "translateX(-33%)"} scale(${numeralReveal.isVisible ? 1 : 0.95})`,
        WebkitTextStroke: "1px var(--secondary)",
        WebkitTextFillColor: "transparent"
      }}>
          {stage.number}
        </div>

        <div className="container mx-auto px-6 md:px-10 lg:px-16 relative">
          <div className={`grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-center ${stage.reverse ? "lg:[direction:rtl]" : ""}`}>
            <div ref={imageReveal.ref} className={`lg:col-span-5 ${stage.reverse ? "lg:[direction:ltr]" : ""}`} style={{
            clipPath: imageReveal.isVisible ? "inset(0% 0% 0% 0%)" : stage.reverse ? "inset(0% 0% 0% 100%)" : "inset(0% 100% 0% 0%)",
            transition: "clip-path 1.2s cubic-bezier(0.22, 1, 0.36, 1)"
          }}>
              <div className="overflow-hidden rounded-lg shadow-lg">
                <div className="aspect-[4/5] w-full">
                  <img src={stage.image} data-wvc-srcset={stage.srcset} data-wvc-sizes={stage.sizes} alt={stage.alt} loading="lazy" decoding="async" className="w-full h-full object-cover" />
                </div>
              </div>
            </div>

            <div ref={textReveal.ref} className={`lg:col-span-6 lg:col-start-${stage.reverse ? "1" : "7"} ${stage.reverse ? "lg:[direction:ltr]" : ""}`}>
              <div className="overflow-hidden mb-5">
                <p className="font-default text-xs tracking-[0.25em] text-muted-foreground uppercase transition-transform duration-700 ease-out" style={{
                transform: textReveal.isVisible ? "translateY(0)" : "translateY(110%)"
              }}>
                  {stage.label}
                </p>
              </div>

              <div className="overflow-hidden mb-8">
                <h3 className="font-default text-foreground leading-[1.05] transition-transform duration-1000 ease-out delay-100" style={{
                fontSize: "clamp(2rem, 4.5vw, 2.75rem)",
                transform: textReveal.isVisible ? "translateY(0)" : "translateY(110%)"
              }}>
                  {stage.title}
                  <span className="italic" style={{
                  color: "var(--secondary)"
                }}>
                    {stage.titleItalic}
                  </span>
                </h3>
              </div>

              <div className="overflow-hidden mb-10">
                <p className="font-default text-lg md:text-xl leading-relaxed text-foreground/80 max-w-xl transition-all duration-1000 ease-out delay-200" style={{
                transform: textReveal.isVisible ? "translateY(0)" : "translateY(30%)",
                opacity: textReveal.isVisible ? 1 : 0
              }}>
                  {stage.description}
                </p>
              </div>

              <div className="max-w-md transition-all duration-1000 ease-out delay-300" style={{
              opacity: textReveal.isVisible ? 1 : 0,
              transform: textReveal.isVisible ? "translateY(0)" : "translateY(20px)"
            }}>
                {stage.details.map((detail, di) => <div data-index={di} key={di} className="flex items-baseline justify-between gap-6 py-3 border-t border-border last:border-b">
                    <span className="font-default text-[10px] tracking-[0.25em] text-muted-foreground uppercase">
                      {detail.name}
                    </span>
                    <span className="font-default italic text-base md:text-lg text-foreground text-right">
                      {detail.value}
                    </span>
                  </div>)}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>;
}
export default function RevenueWorkflow() {
  const introReveal = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  const closingReveal = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.3
  });
  return <section data-section-id="4989" id="rateparityworkflow" className="bg-background text-foreground relative overflow-hidden">
      <div ref={introReveal.ref} className="container mx-auto px-6 md:px-10 lg:px-16 py-24 md:py-32">
        <div className="overflow-hidden mb-12">
          <p className="font-default text-xs tracking-[0.3em] text-muted-foreground uppercase transition-transform duration-700 ease-out" style={{
          transform: introReveal.isVisible ? "translateY(0)" : "translateY(110%)"
        }}>
            Our Proven Revenue Methodology
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-end">
          <div className="lg:col-span-10 lg:col-start-1">
            <div className="overflow-hidden">
              <h2 className="font-default text-foreground leading-[0.95] tracking-tight transition-transform duration-1000 ease-out" style={{
              fontSize: "clamp(3rem, 9vw, 8.5rem)",
              transform: introReveal.isVisible ? "translateY(0)" : "translateY(110%)"
            }}>
                From data
              </h2>
            </div>
            <div className="overflow-hidden mt-2 md:mt-4 pl-[10%] md:pl-[22%]">
              <h2 className="font-default text-foreground leading-[0.95] tracking-tight transition-transform duration-1000 ease-out delay-200" style={{
              fontSize: "clamp(3rem, 9vw, 8.5rem)",
              transform: introReveal.isVisible ? "translateY(0)" : "translateY(110%)"
            }}>
                to{" "}
                <span className="italic" style={{
                color: "var(--secondary)"
              }}>
                  profitability.
                </span>
              </h2>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 mt-16 md:mt-20">
          <div className="lg:col-span-5 lg:col-start-7">
            <div className="overflow-hidden">
              <p className="font-default text-lg md:text-xl leading-relaxed text-foreground/75 transition-all duration-1000 ease-out delay-300" style={{
              opacity: introReveal.isVisible ? 1 : 0,
              transform: introReveal.isVisible ? "translateY(0)" : "translateY(30px)"
            }}>
                Seven strategic stages designed to maximize your hotel's revenue.
                We combine deep industry expertise with data-driven insights to
                ensure your property achieves its full financial potential.
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-6 md:px-10 lg:px-16">
        <div className="h-px w-full bg-border" />
      </div>

      <div>
        {stages.map((stage, i) => <StageSpread key={stage.number} stage={stage} index={i} />)}
      </div>

      <div ref={closingReveal.ref} className="container mx-auto px-6 md:px-10 lg:px-16 py-24 md:py-40">
        <div className="max-w-4xl mx-auto text-center">
          <div className="flex justify-center mb-12">
            <div className="h-20 w-px bg-border" />
          </div>
          <div className="overflow-hidden">
            <p className="font-default italic text-foreground leading-[1.15] transition-all duration-1000 ease-out" style={{
            fontSize: "clamp(1.75rem, 4vw, 3rem)",
            opacity: closingReveal.isVisible ? 1 : 0,
            transform: closingReveal.isVisible ? "translateY(0)" : "translateY(40px)"
          }}>
              And then — only then — does your revenue{" "}
              <span style={{
              color: "var(--secondary)"
            }}>thrive.</span>
            </p>
          </div>
        </div>
      </div>
    </section>;
}